import { Moon, Sun } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * The app's theme, as a runtime choice.
 *
 * ── What this replaces ──
 * There were previously TWO mechanisms deciding the theme and neither was a choice: a
 * hardcoded `data-theme="dark"` on <html> in index.html, and `DarkPinned.tsx`, a per-route
 * pin whose list had been empty for months. They interacted badly enough to be worth
 * recording — the pin implied the app defaulted to light, the attribute silently made every
 * route dark, and reading either file alone gave you the wrong answer. Both are gone. This is
 * the only thing that sets `data-theme` now.
 *
 * ── The three rules ──
 *  1. `:root` IS the light theme. "Light" means NO attribute, not `data-theme="light"` — that
 *     selector does not exist in theme.css and adding it would invent a second source of truth.
 *  2. The pre-paint script in index.html owns the FIRST value. See `readInitial`.
 *  3. An explicit choice is sticky; the absence of one follows the OS, live.
 */
export type Theme = 'light' | 'dark'

/**
 * Shared with the inline script in index.html, which cannot import from here — it runs before
 * any module does. Changing this string means changing it in both places, which is why it is
 * called out rather than merely exported.
 */
export const THEME_STORAGE_KEY = 'crystal-theme'

interface ThemeCtx {
  theme: Theme
  /** Sets an explicit preference. This is sticky — it stops the OS from being consulted. */
  setTheme: (t: Theme) => void
  toggle: () => void
}

const Ctx = createContext<ThemeCtx | null>(null)

/**
 * The initial theme is READ FROM THE DOM, never computed here.
 *
 * The inline script in index.html has already resolved it — from localStorage, falling back to
 * `prefers-color-scheme` — and applied it, because a theme decided in React is a theme decided
 * one paint too late: the page renders light, mounts, and flips to dark in front of the user.
 *
 * Recomputing the same answer here would usually agree, and the one case where it would not is
 * the case that matters: if the script is edited and the two rules drift apart, React would
 * silently overwrite the DOM on mount and reintroduce exactly the flash the script exists to
 * prevent. Reading what the script decided means there is one rule, in one place, and this
 * function cannot disagree with it.
 */
function readInitial(): Theme {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

/** Whether the user has made an explicit choice, as opposed to riding the OS setting. */
function hasStoredChoice(): boolean {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return v === 'light' || v === 'dark'
  } catch {
    // Safari in private mode throws on localStorage access rather than returning null.
    return false
  }
}

/** Apply to the DOM. `light` REMOVES the attribute — see rule 1 above. */
function applyTheme(t: Theme) {
  const el = document.documentElement
  if (t === 'dark') el.setAttribute('data-theme', 'dark')
  else el.removeAttribute('data-theme')
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitial)

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    applyTheme(t)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, t)
    } catch {
      // A theme that does not persist still has to work for this session.
    }
  }, [])

  /*
   * Follow the OS, but only while the user has not overridden it.
   *
   * The listener stays mounted rather than being conditionally attached: `hasStoredChoice` is
   * checked when the event FIRES, not when the effect runs, so a user who toggles and then
   * changes their OS setting is not overridden by a listener that was registered before they
   * made their choice. Conditioning the subscription instead would need the effect to re-run on
   * every theme change to stay correct, which is a subtler way to write the same thing.
   */
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => {
      if (hasStoredChoice()) return
      const next: Theme = e.matches ? 'dark' : 'light'
      setThemeState(next)
      applyTheme(next)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const value = useMemo<ThemeCtx>(
    () => ({ theme, setTheme, toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark') }),
    [theme, setTheme],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * Throws outside the provider rather than defaulting to light.
 *
 * A silent default here would give a component a theme that disagrees with the DOM, and the
 * symptom — a chart or an MUI surface stuck in the wrong palette while the CSS around it is
 * correct — reads as a theming bug in that component rather than as a missing provider.
 */
export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}

/**
 * The toggle itself.
 *
 * ── Why it is a plain <button> and not <Button variant="secondary"> ──
 * It takes the same tokens, the same 40px height and the same 12px round, so it sits as a
 * sibling to Portfolio and Connect and cannot drift from them. What it does NOT take is
 * `Button`'s motion wrapper: `whileHover={{ y: -1 }}` on a square icon control beside two
 * labelled pills makes the row bounce unevenly, and the lift reads as "this submits something".
 * Square, so the icon is optically centred rather than sitting in a 4.5-unit-wide pill.
 *
 * ── The icon shows the DESTINATION, not the current state ──
 * A sun while light and a moon while dark is the other common convention and it is worse here:
 * the control's job is to answer "what happens if I press this", and an icon restating a state
 * the entire screen already communicates answers a question nobody has. `aria-label` and the
 * tooltip say it in words, since an icon alone cannot disambiguate the two conventions.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggle } = useTheme()
  const toDark = theme === 'light'
  const label = toDark ? 'Switch to dark theme' : 'Switch to light theme'

  return (
    <button
      type="button"
      onClick={toggle}
      title={label}
      aria-label={label}
      /*
       * `aria-pressed` rather than a role of switch: this is a two-state control whose states
       * are peers, and a screen reader announcing "dark theme, pressed" is the clearest reading
       * of that. The label names the ACTION, so pressed-state and label together stay coherent.
       */
      aria-pressed={theme === 'dark'}
      className={[
        'inline-flex size-10 shrink-0 select-none items-center justify-center rounded-[12px]',
        'text-ink [background:var(--btn-ghost-bg)] [box-shadow:var(--btn-ghost-shadow)]',
        'transition-[box-shadow,background,color] duration-150',
        'hover:[background:var(--btn-ghost-bg-hi)]',
        className ?? '',
      ].join(' ')}
    >
      {toDark ? <Moon className="size-4" aria-hidden /> : <Sun className="size-4" aria-hidden />}
    </button>
  )
}
